// MexicoV2Card.tsx
import React, { useState } from "react";
import i18n from "../../../i18n";
import { Share2, MessageCircleHeart, Copy, Check, Download } from "lucide-react";
import {
  SHELL_COLORS_MEXICO_V2,
  MEXICO_V2_HEADING_FONT,
  MEXICO_V2_CARD_RADIUS,
  MEXICO_V2_CARD_SHADOW,
  MEXICO_V2_LINE_HEIGHT,
  MEXICO_V2_MODULES,
  MEXICO_V2_BUTTONS,
} from "./AstriaMexicoV2Shell";

// ─── Astria Mexico v2 — result-card primitives ─────────────────────────────
// Unlike the other v2 lanes (which reuse AstriaIndiaV2ResultCard's generic
// share row), the client's MX v2 UI JSON names exact colors for three result
// "modules" (reflection / emotional_thread / gentle_note) and four "buttons"
// (share_whatsapp / share_instagram / copy / save) — see
// AstriaMexicoV2Shell.tsx's MEXICO_V2_MODULES / MEXICO_V2_BUTTONS, copied
// verbatim from that JSON. This card renders the single check-in result
// inside the "reflection" module styling (the module a soft-warm reflective
// check-in maps to) and a share row using the client's literal button
// colors, radius (12), card_radius (14), and card_shadow.

interface MexicoV2CardProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  shareText?: string;
}

export const MexicoV2ResultCard: React.FC<MexicoV2CardProps> = ({
  icon,
  title,
  subtitle,
  children,
  shareText,
}) => {
  const mod = MEXICO_V2_MODULES.reflection;
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        background: SHELL_COLORS_MEXICO_V2.card,
        border: `1px solid ${SHELL_COLORS_MEXICO_V2.cardBorder}`,
        borderRadius: MEXICO_V2_CARD_RADIUS,
        boxShadow: MEXICO_V2_CARD_SHADOW,
      }}
    >
      {/* Module header — ui_identity.modules.reflection.header_color */}
      <div
        className="flex items-center gap-3"
        style={{
          background: mod.headerColor,
          padding: "16px 22px",
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 12,
            background: "rgba(255,255,255,0.28)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#FFFFFF",
              fontFamily: MEXICO_V2_HEADING_FONT,
              lineHeight: 1.25,
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.85)",
                marginTop: 2,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Module body — ui_identity.modules.reflection.body_color / text_color */}
      <div
        style={{
          background: mod.bodyColor,
          color: mod.textColor,
          padding: 22,
          lineHeight: MEXICO_V2_LINE_HEIGHT,
        }}
      >
        {children}
        <MexicoV2ShareRow text={shareText} />
      </div>
    </div>
  );
};

interface ShareRowProps {
  text?: string;
}

export const MexicoV2ShareRow: React.FC<ShareRowProps> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  if (!text) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard unavailable — silently ignore
    }
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleInstagram = () => {
    // Instagram has no direct web share-text intent; copy to clipboard so
    // the user can paste into their Story/DM, and open Instagram.
    handleCopy();
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };

  const wa = MEXICO_V2_BUTTONS.share_whatsapp;
  const ig = MEXICO_V2_BUTTONS.share_instagram;
  const cp = MEXICO_V2_BUTTONS.copy;
  const sv = MEXICO_V2_BUTTONS.save;

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        marginTop: 18,
        paddingTop: 16,
        borderTop: `1px solid ${SHELL_COLORS_MEXICO_V2.cardBorder}`,
        flexWrap: "wrap",
      }}
    >
      <button
        onClick={handleWhatsApp}
        className="flex items-center gap-1.5 text-xs font-semibold transition-all hover:scale-[1.03]"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          borderRadius: wa.radius,
          background: wa.color,
          color: wa.textColor,
        }}
      >
        <Share2 className="w-3.5 h-3.5" />
        WhatsApp
      </button>
      <button
        onClick={handleInstagram}
        className="flex items-center gap-1.5 text-xs font-semibold transition-all hover:scale-[1.03]"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          borderRadius: ig.radius,
          background: ig.color,
          color: ig.textColor,
        }}
      >
        <Share2 className="w-3.5 h-3.5" />
        Instagram
      </button>
      <button
        onClick={handleCopy}
        className="flex items-center gap-1.5 text-xs font-semibold transition-all hover:scale-[1.03]"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          borderRadius: cp.radius,
          background: cp.color,
          color: cp.textColor,
        }}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? "Copiado" : "Copy"}
      </button>
      <button
        disabled
        title={i18n.t("tabErr.comingSoon")}
        className="flex items-center gap-1.5 text-xs font-semibold transition-all opacity-60 cursor-not-allowed"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          borderRadius: sv.radius,
          background: sv.color,
          color: sv.textColor,
        }}
      >
        <Download className="w-3.5 h-3.5" />
        Save Image
      </button>
    </div>
  );
};

export const MexicoV2LoadingState: React.FC<{ label?: string }> = ({
  label = "Reading your check-in…",
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "70px 20px",
      gap: 16,
    }}
  >
    <div
      style={{
        width: 30,
        height: 30,
        border: `3px solid ${SHELL_COLORS_MEXICO_V2.section}`,
        borderTopColor: SHELL_COLORS_MEXICO_V2.accent,
        borderRadius: "50%",
      }}
      className="animate-spin"
    />
    <p style={{ fontSize: 13.5, color: SHELL_COLORS_MEXICO_V2.mutedColor }}>
      {label}
    </p>
  </div>
);

export const MexicoV2ErrorState: React.FC<{
  message: string;
  onRetry: () => void;
}> = ({ message, onRetry }) => (
  <div
    style={{
      padding: "18px 20px",
      background: "#FBEDE9",
      border: "1px solid #E8C6BB",
      borderRadius: 14,
      color: "#95493A",
      fontSize: 14,
    }}
  >
    {message}
    <button
      onClick={onRetry}
      style={{
        display: "block",
        marginTop: 10,
        color: SHELL_COLORS_MEXICO_V2.accent,
        fontWeight: 700,
        fontSize: 13,
      }}
    >
      Try Again
    </button>
  </div>
);

export const MexicoV2ChatCta: React.FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <button
    onClick={onClick}
    className="flex items-center justify-center gap-2 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.02]"
    style={{
      marginTop: 24,
      width: "100%",
      background: `linear-gradient(135deg, ${SHELL_COLORS_MEXICO_V2.accent} 0%, ${SHELL_COLORS_MEXICO_V2.amber} 100%)`,
      boxShadow: `0 6px 20px ${SHELL_COLORS_MEXICO_V2.accentSoft}`,
      paddingTop: 14,
      paddingBottom: 14,
    }}
  >
    <MessageCircleHeart className="w-4 h-4" />
    Continue in Chat
  </button>
);
